import React, { useState, useEffect } from "react";
import MarkdownIt from "markdown-it";
import {
    InputGroup,
    Button,
    Form,
    Card,
    Col,
    Badge,
    Container,
    Row,
    Image,
} from "react-bootstrap";
import { Search } from "react-bootstrap-icons";

interface Post {
    title: string;
    date: string;
    slug: string;
    hero?: string;
    tags: string[];
    content: string;
}

const Blog: React.FC = () => {
    const [route, setRoute] = useState<string>("/");
    const [query, setQuery] = useState<string>("");
    const md = new MarkdownIt({ html: true, linkify: true });

    // ---------------------
    // POSTS DATA
    // ---------------------
    const posts: Post[] = [
        {
            title: "Welcome to MadeByRosita",
            date: "2025-11-10",
            slug: "welcome-slow-making",
            hero: "/images/hot.png",
            tags: ["intro", "mindful-making", "business", "motivation", "new-ventures"],
            content: `It’s time to do what I’ve always wanted. I’m starting my own business finally, at the ripe age of 31, I’m starting to take myself seriously. 

  

For a bit of context: I spent the last three years teaching a university module of my own creation, before being made redundant due to funding cuts. In my (admittedly biased) opinion, it was the best module there. It was called Materials and Making, and it combined material science with traditional and experimental making techniques — blacksmithing, woodworking, ceramics, knitting, and knotting, alongside exploring mycelium as a material, 3D printing, laser cutting, and even making bioplastic from potatoes. Where else could you learn all that in one module? I genuinely don’t think anywhere.
  

I’ve taken the redundancy on the chin (mostly!) and now I want to continue this work independently, offering it to the public. The challenge, of course, is that I now have to find my own students and fund myself — so marketing and business are things I *need* to learn, fast.
  

Something I didn’t expect to discover through all this is just how much self-confidence matters. I really have to back myself now, and show what I can offer on my own as an individual to the world. This process is both empowering and draining as it feels like a constant battle with my own naturally self-critical thoughts.

  
This is very much like starting a new knitting project, or learning a new making skill for the first time. It is going to take time, effort, creativity a lot of picking it up and putting it down again! Wish me luck, dear reader 🙏

If you'd like to suggest a topic or join a workshop, please email hello@madebyrosita.com.`,
        },

    ];

    // ---------------------
    // HELPER FUNCTIONS
    // ---------------------
    const formatDate = (d: string): string => {
        const dt = new Date(d);
        return isNaN(dt.getTime())
            ? d
            : dt.toLocaleDateString(undefined, {
                year: "numeric",
                month: "short",
                day: "numeric",
            });
    };

    const getPost = (slug: string): Post | undefined =>
        posts.find((p) => p.slug === slug);

    const filteredPosts: Post[] = posts
        .filter((p) => {
            if (!query) return true;
            const q = query.toLowerCase();
            return (
                p.title.toLowerCase().includes(q) ||
                p.tags.join(" ").toLowerCase().includes(q) ||
                p.content.toLowerCase().includes(q)
            );
        })
        .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

    // ---------------------
    // HASH ROUTING
    // ---------------------
    useEffect(() => {
        const handleHash = (): void => {
            setRoute(window.location.hash || "/");
        };
        window.addEventListener("hashchange", handleHash);
        handleHash();
        return () => window.removeEventListener("hashchange", handleHash);
    }, []);

    // ---------------------
    // ROUTE: SINGLE POST
    // ---------------------
    if (route.startsWith("#/post/")) {
        const slug = route.replace("#/post/", "");
        const post = getPost(slug);

        if (!post)
            return (
                <Container className="py-5 text-center">
                    <p>Post not found.</p>
                    <Button variant="link" href="#" className="p-0 text-decoration-none">
                        ← Back to blog
                    </Button>
                </Container>
            );

        return (
            <Container className="py-5">
                <Row className="justify-content-center">
                    <Col md={8} lg={7}>
                        <nav className="mb-3">
                            <Button
                                variant="link"
                                href="#blog"
                                className="p-0 text-decoration-none text-muted"
                            >
                                ← Back
                            </Button>
                        </nav>

                        <Card className="border-0 shadow-sm">
                            {post.hero && (
                                <Image
                                    src={post.hero}
                                    alt={post.title}
                                    fluid
                                    rounded
                                    className="mb-4"
                                    style={{ objectFit: "cover", maxHeight: "350px", width: "100%" }}
                                />
                            )}

                            <Card.Body>
                                <Card.Title as="h1" className="h3 fw-bold text-dark mb-2">
                                    {post.title}
                                </Card.Title>

                                <Card.Text className="text-muted small mb-3">
                                    {formatDate(post.date)}
                                </Card.Text>

                                <Card.Text
                                    as="div"
                                    dangerouslySetInnerHTML={{ __html: md.render(post.content) }}
                                />

                                <div className="mt-4 d-flex flex-wrap gap-2">
                                    {post.tags.map((t) => (
                                        <Badge key={t} bg="light" text="dark" className="border">
                                            #{t}
                                        </Badge>
                                    ))}
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            </Container>
        );
    }

    // ---------------------
    // ROUTE: BLOG INDEX
    // ---------------------
    return (
        <Container fluid className="bg-light min-vh-100">
            {/* Header */}
            <header className="text-center mb-5">
                <h1 className="h2 fw-bold text-dark">Blog</h1>
                <p className="text-muted small">
                    Notes on slow making, materials, and mindful craft.
                </p>
            </header>

            {/* Search */}
            <Container className="mb-4">
                <Form>
                    <InputGroup className="shadow-sm">
                        <InputGroup.Text className="bg-white border-end-0">
                            <Search className="text-muted" />
                        </InputGroup.Text>
                        <Form.Control
                            type="search"
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder="Search blog posts..."
                            aria-label="Search posts"
                            className="border-start-0"
                        />
                        {query && (
                            <Button
                                variant="outline-secondary"
                                onClick={() => setQuery("")}
                                className="border-start-0"
                            >
                                ✕
                            </Button>
                        )}
                    </InputGroup>
                </Form>
            </Container>

            {/* Posts Grid */}
            <Container>
                {filteredPosts.length === 0 ? (
                    <p>
                        No posts yet. Email with me your suggestions{" "}
                        <a href="mailto:hello@madebyrosita.com">hello@madebyrosita.com</a> ✨
                    </p>
                ) : (
                    <Row className="g-4">
                        {filteredPosts.map((p) => (
                            <Col key={p.slug} md={4} className="d-flex">
                                <Card className="shadow-sm border-0 w-100 h-100">
                                    {p.hero && (
                                        <a href={`#/post/${p.slug}`}>
                                            <Card.Img
                                                variant="top"
                                                src={p.hero}
                                                alt={p.title}
                                                style={{ objectFit: "cover", height: "200px" }}
                                            />
                                        </a>
                                    )}

                                    <Card.Body>
                                        <Card.Title as="h5" className="fw-semibold">
                                            <a
                                                href={`#/post/${p.slug}`}
                                                className="stretched-link text-decoration-none text-dark"
                                            >
                                                {p.title}
                                            </a>
                                        </Card.Title>

                                        <Card.Text className="text-muted small mb-2">
                                            {formatDate(p.date)}
                                        </Card.Text>

                                        <Card.Text className="small">
                                            {p.content.replace(/[#>*_`~[\]()!-]/g, "").slice(0, 120)}...
                                        </Card.Text>

                                        <div className="mt-2 d-flex flex-wrap gap-1">
                                            {p.tags.map((t) => (
                                                <Badge key={t} bg="light" text="dark" className="border">
                                                    #{t}
                                                </Badge>
                                            ))}
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>
                        ))}
                    </Row>
                )}
            </Container>
        </Container>
    );
};

export default Blog;
